import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormlyFieldConfig, FormlyFieldProps } from '@ngx-formly/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Observable } from 'rxjs';

import { BaseEditService } from './base';
import { FormatService } from './format.service';
import { LanguageService } from './language.service';
import { MenuService } from './menu.service';
import { UtilityService } from './utility.service';
import { BooleanStrings } from '../constants';
import { FormlySummary, IdDescription } from '../models';
import { FormsValidators } from '../validators';
import { GridComponent } from '../components/grids/grid/grid.component';

export type FormlyProps = FormlyFieldProps & { [additionalProperties: string]: any };
export type FormlyHideExpression = (field: FormlyFieldConfig) => boolean;
export type FormlyExpressionProperties = {
  [property: string]: string | ((field: FormlyFieldConfig) => any) | Observable<any>;
};

@Injectable({
  providedIn: 'root',
})
export class FormlyService {
  isMobile = false;

  constructor(
    @Inject(DOCUMENT) protected document: Document,
    protected formatService: FormatService,
    protected menuService: MenuService,
    protected utilityService: UtilityService,
    protected languageService: LanguageService,
  ) {}

  text(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
    templateHtml?: (model: any) => string,
  ): FormlyFieldConfig {
    if (this.menuService.isReadOnly) {
      props = { ...props, readonly: true, tabindex: -1 };
    }
    return {
      key,
      type: 'input',
      props: {
        label: this.languageService.translate(label, abbreviatedLabel),
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
        'props.template': (field: FormlyFieldConfig) => (templateHtml ? templateHtml(field.model) : undefined),
      },
      validators: { validation: validation ?? [], ...extraValidators },
      asyncValidators: asyncValidators,
    };
  }

  textReadonly(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
    templateHtml?: (model: any) => string,
  ): FormlyFieldConfig {
    return this.text(
      key,
      label,
      abbreviatedLabel,
      { ...props, readonly: true, tabindex: -1 },
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      templateHtml,
    );
  }

  textarea(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'textarea',
      props: {
        label: this.languageService.translate(label, abbreviatedLabel),
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
      validators: { validation: validation ?? [], ...extraValidators },
      asyncValidators,
    };
  }

  textAreaReadonly(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textarea(
      key,
      label,
      abbreviatedLabel,
      { ...props, readonly: true, tabindex: -1 },
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
    );
  }

  password(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
    templateHtml?: (model: any) => string,
  ): FormlyFieldConfig {
    if (this.menuService.isReadOnly) {
      props = { ...props, readonly: true, tabindex: -1 };
    }
    return this.text(
      key,
      label,
      abbreviatedLabel,
      { ...props, type: 'password' },
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      templateHtml,
    );
  }

  date(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.menuService.isReadOnly
      ? this.dateReadonly(
          key,
          label,
          abbreviatedLabel,
          props,
          hideExpression,
          expressionProperties,
          validation,
          extraValidators,
          asyncValidators,
        )
      : {
          key,
          type: 'date',
          props: {
            label: this.languageService.translate(label, abbreviatedLabel),
            ...props,
          },
          expressions: {
            hide: hideExpression,
            ...expressionProperties,
          },
          validators: {
            validation: props?.required ? [FormsValidators.date, ...(validation ?? [])] : validation ?? [],
            ...extraValidators,
          },
          asyncValidators,
        };
  }

  dateReadonly(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textReadonly(
      key,
      label,
      abbreviatedLabel,
      props,
      hideExpression,
      expressionProperties,
      props?.required ? [FormsValidators.date, ...(validation ?? [])] : validation ?? [],
      extraValidators,
      asyncValidators,
      (model: any) => this.formatService.date(model[key]),
    );
  }

  dateTimeReadonly(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textReadonly(
      key,
      label,
      abbreviatedLabel,
      props,
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      (model: any) => this.formatService.dateTime(model[key]),
    );
  }

  number(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = [FormsValidators.number],
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.menuService.isReadOnly
      ? this.numberReadonly(key, label, abbreviatedLabel, props, hideExpression)
      : {
          key,
          type: 'input',
          props: {
            type: 'number',
            label: this.languageService.translate(label, abbreviatedLabel),
            ...props,
          },
          expressions: {
            hide: hideExpression,
            ...expressionProperties,
          },
          validators: { validation: validation ?? [], ...extraValidators },
          asyncValidators,
        };
  }

  numberReadonly(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textReadonly(
      key,
      label,
      abbreviatedLabel,
      props,
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      (model: any) => (model[key] === undefined ? undefined : this.formatService.number(model[key])),
    );
  }

  integerReadonly(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textReadonly(
      key,
      label,
      abbreviatedLabel,
      props,
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      (model: any) => (model[key] === undefined ? undefined : this.formatService.number(model[key], 0)),
    );
  }

  select(
    key: string,
    label: string,
    options: IdDescription[] | Observable<IdDescription[]>,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    if (this.menuService.isReadOnly) {
      props = { ...props, disabled: true };
    }
    return {
      key,
      type: 'select',
      props: {
        label: this.languageService.translate(label, abbreviatedLabel),
        options: this.utilityService.mapSelectOptions(options),
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
      validators: { validation: validation ?? [], ...extraValidators },
      asyncValidators,
    };
  }

  selectReadonly(
    key: string,
    label: string,
    options: IdDescription[] | Observable<IdDescription[]>,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.select(
      key,
      label,
      options,
      abbreviatedLabel,
      { ...props, disabled: true },
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
    );
  }

  checkbox(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.menuService.isReadOnly
      ? this.readonlyBoolean(
          key,
          label,
          abbreviatedLabel,
          props,
          hideExpression,
          expressionProperties,
          validation,
          extraValidators,
          asyncValidators,
        )
      : {
          key,
          type: 'checkbox',
          props: {
            hideLabel: true,
            indeterminate: false,
            label: this.languageService.translate(label, abbreviatedLabel),
            ...props,
          },
          expressions: {
            hide: hideExpression,
            ...expressionProperties,
          },
          validators: { validation: validation ?? [], ...extraValidators },
          asyncValidators,
        };
  }

  readonlyBoolean(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = [FormsValidators.number],
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textReadonly(
      key,
      label,
      abbreviatedLabel,
      props,
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      (model: any) => (model[key] ? BooleanStrings.translatedYes : BooleanStrings.translatedNo),
    );
  }

  radio(
    key: string,
    name: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.menuService.isReadOnly
      ? this.textReadonly(
          key,
          label,
          abbreviatedLabel,
          props,
          hideExpression,
          expressionProperties,
          validation,
          extraValidators,
          asyncValidators,
        )
      : {
          key,
          type: 'radio',
          props: {
            hideLabel: true,
            indeterminate: false,
            label: this.languageService.translate(label, abbreviatedLabel),
            name,
            ...props,
          },
          expressions: {
            hide: hideExpression,
            ...expressionProperties,
          },
          validators: { validation: validation ?? [], ...extraValidators },
          asyncValidators,
        };
  }

  fileUpload(
    text: string,
    select: (model: any) => void,
    dropDown?: (model: any) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return {
      type: 'file-upload',
      props: {
        hideLabel: true,
        text,
        select,
        dropDown,
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
    };
  }

  button(
    text: string,
    onClick: (model: any) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return {
      type: 'button',
      props: {
        hideLabel: true,
        text,
        onClick,
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
    };
  }

  importButton(
    text: string,
    select: (model: any) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return {
      type: 'import-button',
      props: {
        hideLabel: true,
        text,
        select,
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
    };
  }

  createButton(
    onClick: (model: any) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return this.button(
      $localize`Create`,
      (model: any) => onClick(model),
      { iconClass: 'fa fa-plus', ...props },
      this.menuService.isReadOnly ? () => true : hideExpression,
      expressionProperties,
    );
  }

  exportButton(
    grid: GridComponent,
    fileName: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return this.button(
      $localize`Export`,
      () => grid.export(fileName),
      { btnClass: 'success', iconClass: 'far fa-file-excel', ...props },
      hideExpression,
      expressionProperties,
    );
  }

  exportButtonNoGridComponent(
    onClick: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return this.button(
      $localize`Export`,
      () => onClick(),
      { btnClass: 'success', iconClass: 'far fa-file-excel', ...props },
      hideExpression,
      expressionProperties,
    );
  }

  printButton(
    onClick: (model: any) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return this.button(
      $localize`Print`,
      (model: any) => onClick(model),
      { iconClass: 'fa fa-print', ...props },
      this.menuService.isReadOnly ? () => true : hideExpression,
      expressionProperties,
    );
  }

  backButton(route: ActivatedRoute, path?: string) {
    return {
      type: 'back-button',
      props: {
        route,
        path,
      },
    };
  }

  readonly(
    key: string,
    label: string,
    abbreviatedLabel?: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    templateHtml?: (model: any) => string,
  ): FormlyFieldConfig {
    return {
      type: 'template',
      props: {
        label: this.languageService.translate(label, abbreviatedLabel),
        ...props,
      },
      expressions: {
        hide: hideExpression,
        'props.template': (field: FormlyFieldConfig) => (templateHtml ? templateHtml(field.model) : field.model?.[key]),
      },
    };
  }

  template(
    label: string,
    template: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig {
    return {
      type: 'template',
      props: {
        label,
        template,
        ...props,
      },
      expressions: {
        hide: hideExpression,
      },
    };
  }

  requiredFieldValidation(key: string, label: string) {
    return this.fieldValidation(key, label, { required: true }, (field: FormlyFieldConfig) => !!field.model?.[key]);
  }

  fieldValidation(key: string, label: string, props?: FormlyProps, hideExpression?: FormlyHideExpression) {
    return {
      key,
      type: 'template',
      props: {
        ...props,
        label,
        hideLabel: true,
      },
      hideExpression: hideExpression,
    };
  }

  warningMessage(message: string, hideExpression?: FormlyHideExpression) {
    return {
      type: 'template',
      props: {
        template: `<div class="error-message">${message}</div>`,
      },
      expressions: {
        hide: hideExpression,
      },
    };
  }

  lineBreak() {
    return {
      type: 'line-break',
    };
  }

  fields(label: string, fields: any[], props?: FormlyProps, hideExpression?: FormlyHideExpression): FormlyFieldConfig {
    fields.forEach((x) => {
      x.wrappers = [];
    });

    return {
      type: 'fields',
      fieldGroup: fields,
      fieldGroupClassName: 'row',
      props: {
        label,
        ...props,
      },
      expressions: {
        hide: hideExpression,
      },
    };
  }

  array(
    key: string,
    fields: FormlyFieldConfig[],
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'array',
      fieldArray: {
        fieldGroup: fields,
      },
      props,
      expressions: {
        hide: hideExpression,
      },
    };
  }

  table(
    key: string,
    fields: FormlyFieldConfig[],
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig {
    fields.forEach((field: FormlyFieldConfig) => (field.wrappers = []));
    return {
      key,
      type: 'repeat',
      fieldArray: {
        fieldGroup: fields,
      },
      props,
      expressions: {
        hide: hideExpression,
      },
    };
  }

  summary(fields: FormlySummary[], props?: FormlyProps, hideExpression?: FormlyHideExpression): FormlyFieldConfig {
    fields.forEach((field: FormlyFieldConfig) => (field.wrappers = []));
    return {
      type: 'summary',
      props: {
        fields,
        ...props,
      },
      expressions: {
        hide: hideExpression,
      },
    };
  }

  inlineFormLineBreak(): FormlyFieldConfig {
    return {
      type: 'template',
      className: 'flex-break',
    };
  }

  mobileSplitFields(fields: FormlyFieldConfig[]) {
    fields.forEach((field: FormlyFieldConfig) => {
      field.props.mobileSplit = true;
    });
    return fields;
  }

  oneColumnGrid(column1: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-xl-6',
            fieldGroup: column1,
          },
        ],
      },
    ];
  }

  twoColumnGrid(column1: FormlyFieldConfig[], column2: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-xl-6',
            fieldGroup: column1,
          },
          {
            className: 'col-xl-6',
            fieldGroup: column2,
          },
        ],
      },
    ];
  }

  threeColumnGrid(
    column1: FormlyFieldConfig[],
    column2: FormlyFieldConfig[],
    column3: FormlyFieldConfig[],
  ): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-xl-4',
            fieldGroup: column1,
          },
          {
            className: 'col-xl-4',
            fieldGroup: column2,
          },
          {
            className: 'col-xl-4',
            fieldGroup: column3,
          },
        ],
      },
    ];
  }

  fourColumnGrid(
    column1: FormlyFieldConfig[],
    column2: FormlyFieldConfig[],
    column3: FormlyFieldConfig[],
    column4: FormlyFieldConfig[],
  ): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-xl-3',
            fieldGroup: column1,
          },
          {
            className: 'col-xl-3',
            fieldGroup: column2,
          },
          {
            className: 'col-xl-3',
            fieldGroup: column3,
          },
          {
            className: 'col-xl-3',
            fieldGroup: column4,
          },
        ],
      },
    ];
  }

  twoColumnMobileGrid(column1: FormlyFieldConfig[], column2: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-6',
            fieldGroup: this.mobileSplitFields(column1),
          },
          {
            className: 'col-6',
            fieldGroup: this.mobileSplitFields(column2),
          },
        ],
      },
    ];
  }

  search(
    key: string,
    label?: string,
    service?: BaseEditService<any, any>,
    results?: (results: Observable<any[]>) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'input-search',
      props: {
        label,
        service,
        results,
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
    };
  }

  modalSearch(
    key: string,
    label: string,
    service: BaseEditService<any, any>,
    columnDefs: ColDef[],
    id?: (model: any) => any,
    allLabel?: string,
    createComponent?: any,
    selected?: (model: any) => void,
    clear?: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    id2?: (model: any) => any,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'input-search',
      props: {
        label,
        allLabel,
        service,
        selected,
        clear,
        columnDefs,
        openModal: true,
        editButton: false,
        createComponent,
        ...props,
        required: false,
      },
      expressions:
        id && id2
          ? {
              hide: this.menuService.isReadOnly ? () => true : hideExpression,
              'props.id': (field: FormlyFieldConfig) => id(field.model),
              'props.id2': (field: FormlyFieldConfig) => id2(field.model),
            }
          : id
            ? {
                hide: this.menuService.isReadOnly ? () => true : hideExpression,
                'props.id': (field: FormlyFieldConfig) => id(field.model),
              }
            : {
                hide: this.menuService.isReadOnly ? () => true : hideExpression,
              },
    };
  }

  focusElement(id: string) {
    setTimeout(() => {
      const input: HTMLElement = document.querySelector(`input[id*="_${id}_"]`);
      input?.focus();
    }, 0);
  }

  selectElement(id: string) {
    setTimeout(() => {
      const input: HTMLInputElement = document.querySelector(`input[id*="_${id}_"]`);
      input?.select();
    }, 0);
  }
}
